import type { VenueOSClient, VenueOSContext } from '@mappedin/venueos-applet';
import { useEffect, useState } from 'react';
import type { Venue, VenueSource } from '../types';

interface RawVenue {
  id: string;
  name?: string;
  latitude?: number | null;
  longitude?: number | null;
  slug?: string;
  _source?: VenueSource;
}

interface UseVenuesProps {
  client: VenueOSClient | null;
  context: VenueOSContext | null;
}

interface UseVenuesReturn {
  venues: Venue[];
  isLoading: boolean;
  error: string | null;
}

function extractRawVenues(data: unknown): RawVenue[] {
  if (Array.isArray(data)) return data as RawVenue[];
  if (data && typeof data === 'object' && 'venues' in data) {
    const list = (data as { venues?: unknown }).venues;
    if (Array.isArray(list)) return list as RawVenue[];
  }
  return [];
}

function toGeolocatedVenue(raw: RawVenue): Venue | null {
  if (typeof raw.latitude !== 'number' || typeof raw.longitude !== 'number') return null;
  const venue: Venue = {
    id: raw.id,
    name: raw.name ?? raw.id,
    latitude: raw.latitude,
    longitude: raw.longitude,
  };
  if (raw.slug) venue.slug = raw.slug;
  if (raw._source) venue._source = raw._source;
  return venue;
}

/**
 * Fetch the org's venue list via the VenueOS applet SDK and narrow it to
 * venues that have geographic coordinates (transit lookup needs lat/lng).
 *
 * Returns an empty list while the applet is still initializing or when the
 * request fails — `error` carries the failure reason for the UI.
 */
export function useVenues({ client, context }: UseVenuesProps): UseVenuesReturn {
  const [venues, setVenues] = useState<Venue[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const orgId = context?.org.id ?? null;

  useEffect(() => {
    if (!client || !orgId) {
      setVenues([]);
      setIsLoading(client !== null);
      return undefined;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const load = async (): Promise<void> => {
      try {
        const response = await client.api.fetch(`/api/organizations/${orgId}/venues`);
        if (cancelled) return;
        if (!response.ok) {
          setError(`Failed to fetch venues: ${response.status}`);
          setVenues([]);
          return;
        }
        const list = extractRawVenues(response.data)
          .map(toGeolocatedVenue)
          .filter((v): v is Venue => v !== null);
        setVenues(list);
      } catch (err: unknown) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch venues');
        setVenues([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [client, orgId]);

  return { venues, isLoading, error };
}
